
import { useEffect, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Loader2, ChevronRight, ChevronLeft, Check, Plus } from 'lucide-react';
import { ImageUploadDropzone } from './image-upload-dropzone';
import { TagsInput } from '@/components/ui/tags-input';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';

const STREAM_FORM_DRAFT_KEY = 'admin-stream-form-draft-v1';

const STEPS = [
    { id: 'basic', title: 'Basic Info', fields: ['title', 'description', 'tags'] },
    { id: 'schedule', title: 'Schedule', fields: ['scheduledDate', 'scheduledTime', 'visibility', 'segmentId'] },
    { id: 'media', title: 'Media', fields: ['thumbnailUrl', 'featureImageUrl'] },
] as const;

type StepId = (typeof STEPS)[number]['id'];

const formSchema = z.object({
    title: z.string().min(2, {
        message: "Title must be at least 2 characters.",
    }),
    description: z.string().optional(),
    scheduledDate: z.date(),
    scheduledTime: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, { message: "Invalid time format" }),
    visibility: z.enum(['public', 'members', 'invite_only']),
    segmentId: z.number().int().positive().optional(),
    thumbnailUrl: z.string().optional(),
    featureImageUrl: z.string().optional(),
    tags: z.array(z.string()).optional(),
}).refine((data) => {
    if (data.visibility === 'invite_only') {
        return typeof data.segmentId === 'number' && data.segmentId > 0;
    }
    return true;
}, {
    message: 'Segment is required for invite-only streams',
    path: ['segmentId'],
});

type StreamFormValues = z.infer<typeof formSchema>;

interface SegmentOption {
    id: number;
    name: string;
    memberCount: number;
}

import type { SharedStream } from '@/lib/shared-types';

interface StreamFormProps {
    initialData?: {
        id?: number;
        title: string;
        description?: string;
        scheduledStart?: string;
        inviteRequired?: boolean;
        visibility?: 'public' | 'members' | 'invite_only';
        segmentId?: number;
        thumbnailUrl?: string;
        featureImageUrl?: string;
        tags?: string[];
    };
    onSuccess?: (...args: [SharedStream]) => void;
}

function RequiredLabel({
    children,
    required = false,
}: {
    children: React.ReactNode;
    required?: boolean;
}) {
    return (
        <span className="inline-flex items-center gap-1">
            <span>{children}</span>
            {required && <span className="text-destructive">*</span>}
        </span>
    );
}

export function StreamForm({ initialData, onSuccess }: StreamFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [segments, setSegments] = useState<SegmentOption[]>([]);
    const [showSegmentCreator, setShowSegmentCreator] = useState(false);
    const [newSegmentName, setNewSegmentName] = useState('');
    const [isCreatingSegment, setIsCreatingSegment] = useState(false);
    const [currentStep, setCurrentStep] = useState<StepId>('basic');
    const [completedSteps, setCompletedSteps] = useState<Set<StepId>>(new Set());
    const [draftRestored, setDraftRestored] = useState(false);

    const defaultDate = initialData?.scheduledStart
        ? new Date(initialData.scheduledStart)
        : new Date(Date.now() + 24 * 60 * 60 * 1000);
    const defaultTime = `${defaultDate.getHours().toString().padStart(2, '0')}:${defaultDate.getMinutes().toString().padStart(2, '0')}`;

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        defaultValues: {
            title: initialData?.title || "",
            description: initialData?.description || "",
            visibility: initialData?.visibility || (initialData?.inviteRequired ? 'invite_only' : 'members'),
            segmentId: initialData?.segmentId,
            scheduledDate: defaultDate,
            scheduledTime: defaultTime,
            thumbnailUrl: initialData?.thumbnailUrl || "",
            featureImageUrl: initialData?.featureImageUrl || "",
            tags: initialData?.tags ?? [],
        },
    });

    const visibility = form.watch('visibility');
    const currentStepIndex = STEPS.findIndex(step => step.id === currentStep);
    const isLastStep = currentStepIndex === STEPS.length - 1;
    const isFirstStep = currentStepIndex === 0;
    const shouldPersistDraft = !initialData?.id;

    const clearDraft = () => {
        if (typeof window === 'undefined') return;
        window.localStorage.removeItem(STREAM_FORM_DRAFT_KEY);
    };

    const getStepFields = (stepId: StepId): string[] => {
        const step = STEPS.find(s => s.id === stepId);
        return step ? [...step.fields] : [];
    };

    const handleNext = () => {
        setCompletedSteps(prev => new Set(prev).add(currentStep));
        if (!isLastStep) {
            setCurrentStep(STEPS[currentStepIndex + 1].id);
        }
    };

    const handleBack = () => {
        if (!isFirstStep) {
            setCurrentStep(STEPS[currentStepIndex - 1].id);
        }
    };

    const handleStepChange = (stepId: string) => {
        const targetStep = STEPS.find(step => step.id === stepId);
        if (!targetStep) return;
        setCurrentStep(targetStep.id);
    };

    const loadSegments = async () => {
        try {
            const response = await api.get<{ segments: SegmentOption[] }>('/api/admin/segments');
            if (response.success) {
                setSegments(response.data?.segments || []);
            }
        } catch (error) {
            console.error('Failed to load segments:', error);
        }
    };

    useEffect(() => {
        loadSegments();
    }, []);

    useEffect(() => {
        if (!shouldPersistDraft || typeof window === 'undefined') {
            setDraftRestored(true);
            return;
        }

        try {
            const rawDraft = window.localStorage.getItem(STREAM_FORM_DRAFT_KEY);
            if (!rawDraft) {
                setDraftRestored(true);
                return;
            }

            const draft = JSON.parse(rawDraft) as Partial<StreamFormValues> & {
                currentStep?: StepId;
                completedSteps?: StepId[];
                scheduledDate?: string;
            };

            form.reset({
                ...form.getValues(),
                ...draft,
                scheduledDate: draft.scheduledDate ? new Date(draft.scheduledDate) : form.getValues('scheduledDate'),
            });

            if (draft.currentStep && STEPS.some((step) => step.id === draft.currentStep)) {
                setCurrentStep(draft.currentStep);
            }
            if (Array.isArray(draft.completedSteps)) {
                setCompletedSteps(new Set(draft.completedSteps.filter((step): step is StepId => STEPS.some((item) => item.id === step))));
            }
        } catch (error) {
            console.error('Failed to restore stream form draft:', error);
        } finally {
            setDraftRestored(true);
        }
    }, [form, shouldPersistDraft]);

    useEffect(() => {
        if (!shouldPersistDraft || !draftRestored || typeof window === 'undefined') {
            return;
        }

        const subscription = form.watch((values) => {
            try {
                window.localStorage.setItem(
                    STREAM_FORM_DRAFT_KEY,
                    JSON.stringify({
                        ...values,
                        scheduledDate: values.scheduledDate instanceof Date
                            ? values.scheduledDate.toISOString()
                            : values.scheduledDate,
                        currentStep,
                        completedSteps: Array.from(completedSteps),
                    }),
                );
            } catch (error) {
                console.error('Failed to persist stream form draft:', error);
            }
        });

        return () => subscription.unsubscribe();
    }, [completedSteps, currentStep, draftRestored, form, shouldPersistDraft]);

    const handleCreateSegment = async () => {
        const name = newSegmentName.trim();
        if (!name) {
            toast.error('Enter a segment name');
            return;
        }

        setIsCreatingSegment(true);
        try {
            const response = await api.post<{ segment: SegmentOption }>('/api/admin/segments', { name });
            if (!response.success || !response.data?.segment) {
                toast.error(response.error?.message || 'Failed to create segment');
                return;
            }

            await loadSegments();
            form.setValue('segmentId', response.data.segment.id, { shouldValidate: true });
            setNewSegmentName('');
            setShowSegmentCreator(false);
            toast.success('Segment created');
        } catch (error) {
            console.error('Failed to create segment:', error);
            toast.error('Failed to create segment');
        } finally {
            setIsCreatingSegment(false);
        }
    };

    const onSubmit: SubmitHandler<StreamFormValues> = async (values) => {
        setIsSubmitting(true);
        try {
            const dateTime = new Date(values.scheduledDate);
            const [hours, minutes] = values.scheduledTime.split(':');
            dateTime.setHours(parseInt(hours), parseInt(minutes));

            const tags = values.tags && values.tags.length > 0 ? values.tags : undefined;

            const payload = {
                title: values.title,
                description: values.description,
                scheduledStart: dateTime.toISOString(),
                visibility: values.visibility,
                inviteRequired: values.visibility === 'invite_only',
                segmentId: values.visibility === 'invite_only' ? values.segmentId : undefined,
                thumbnailUrl: values.thumbnailUrl || undefined,
                featureImageUrl: values.featureImageUrl || undefined,
                tags,
            };

            const isEdit = !!initialData?.id;
            const endpoint = isEdit ? `/api/streams/${initialData.id}` : '/api/streams';

            const response = await (isEdit
                ? api.put<{ stream: SharedStream }>(endpoint, payload)
                : api.post<{ stream: SharedStream }>(endpoint, payload)
            );

            if (response.success && response.data?.stream) {
                clearDraft();
                toast.success(isEdit ? 'Stream updated' : 'Stream created');
                if (onSuccess) onSuccess(response.data.stream);
            } else {
                const errorMessage = response.error?.message || 'Operation failed';
                toast.error(errorMessage);
                form.setError('root', { message: errorMessage });
            }
        } catch (error) {
            console.error('Stream form submission error:', error);
            toast.error('Network error');
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Form {...form}>
            <Tabs value={currentStep} onValueChange={handleStepChange} className="mb-6">
                <TabsList className="grid h-auto w-full grid-cols-3 rounded-full bg-muted p-1">
                    {STEPS.map((step, index) => {
                        const isCompleted = completedSteps.has(step.id);
                        const isCurrent = currentStep === step.id;

                        return (
                            <TabsTrigger
                                key={step.id}
                                value={step.id}
                                className={cn(
                                    "flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-all",
                                    isCurrent && "!bg-primary !text-primary-foreground shadow-sm",
                                    isCompleted && !isCurrent && "!text-green-600 dark:!text-green-400",
                                    !isCurrent && !isCompleted && "text-muted-foreground"
                                )}
                            >
                                {isCompleted ? (
                                    <Check className="h-4 w-4" />
                                ) : (
                                    <span className="flex h-4 w-4 items-center justify-center rounded-full border text-xs">
                                        {index + 1}
                                    </span>
                                )}
                                <span className="hidden sm:inline">{step.title}</span>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <TabsContent value="basic" className="mt-0">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Basic Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="title"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel><RequiredLabel required>Title</RequiredLabel></FormLabel>
                                            <FormControl>
                                                <Input placeholder="Sunday Service" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Description</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder="Join us for our weekly service..."
                                                    className="resize-none min-h-[80px]"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="tags"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Tags</FormLabel>
                                            <FormControl>
                                                <TagsInput
                                                    value={field.value ?? []}
                                                    onChange={field.onChange}
                                                    placeholder="Add tag (Enter to confirm)…"
                                                />
                                            </FormControl>
                                            <FormDescription>
                                                Press Enter or comma to add — helps viewers find this stream
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>
                </TabsContent>

                <TabsContent value="schedule" className="mt-0">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Schedule</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="scheduledDate"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-col">
                                                <FormLabel><RequiredLabel required>Date</RequiredLabel></FormLabel>
                                                <Popover modal={true}>
                                                    <PopoverTrigger asChild>
                                                        <FormControl>
                                                            <Button
                                                                variant="outline"
                                                                className={cn(
                                                                    "w-full pl-3 text-left font-normal",
                                                                    !field.value && "text-muted-foreground"
                                                                )}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {field.value ? (
                                                                    format(field.value, "PPP")
                                                                ) : (
                                                                    <span>Pick a date</span>
                                                                )}
                                                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                            </Button>
                                                        </FormControl>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
                                                        <Calendar
                                                            mode="single"
                                                            selected={field.value}
                                                            onSelect={(date) => {
                                                                field.onChange(date);
                                                            }}
                                                            disabled={(date) =>
                                                                date < new Date(new Date().setHours(0, 0, 0, 0))
                                                            }
                                                            initialFocus
                                                        />
                                                    </PopoverContent>
                                                </Popover>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="scheduledTime"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel><RequiredLabel required>Time</RequiredLabel></FormLabel>
                                                <FormControl>
                                                    <Input type="time" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                <FormField
                                    control={form.control}
                                    name="visibility"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel><RequiredLabel required>Visibility</RequiredLabel></FormLabel>
                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select visibility" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="public">Public — Anyone can watch, no login required</SelectItem>
                                                    <SelectItem value="members">Members Only — Must be signed in</SelectItem>
                                                    <SelectItem value="invite_only">Invite Only — Must have explicit access</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormDescription>
                                                Controls who can view this stream
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {visibility === 'invite_only' && (
                                    <FormField
                                        control={form.control}
                                        name="segmentId"
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className="flex items-center justify-between gap-3">
                                                    <FormLabel><RequiredLabel required>Segment</RequiredLabel></FormLabel>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 rounded-full px-3"
                                                        onClick={() => setShowSegmentCreator((prev) => !prev)}
                                                    >
                                                        <Plus className="mr-1 h-3.5 w-3.5" />
                                                        Create
                                                    </Button>
                                                </div>
                                                <Select
                                                    value={field.value ? String(field.value) : ''}
                                                    onValueChange={(value) => field.onChange(parseInt(value, 10))}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Select a segment" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {segments.map((segment) => (
                                                            <SelectItem key={segment.id} value={String(segment.id)}>
                                                                {segment.name} ({segment.memberCount} members)
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {showSegmentCreator && (
                                                    <div className="rounded-xl border border-border/70 bg-background/70 p-3">
                                                        <div className="flex flex-col gap-3 sm:flex-row">
                                                            <Input
                                                                value={newSegmentName}
                                                                onChange={(event) => setNewSegmentName(event.target.value)}
                                                                placeholder="New segment name"
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        event.preventDefault();
                                                                        void handleCreateSegment();
                                                                    }
                                                                }}
                                                            />
                                                            <div className="flex gap-2">
                                                                <Button
                                                                    type="button"
                                                                    onClick={() => void handleCreateSegment()}
                                                                    disabled={isCreatingSegment}
                                                                >
                                                                    {isCreatingSegment && (
                                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                    )}
                                                                    Save
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    onClick={() => {
                                                                        setShowSegmentCreator(false);
                                                                        setNewSegmentName('');
                                                                    }}
                                                                    disabled={isCreatingSegment}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <FormDescription>
                                                    Members in this segment will be granted stream access automatically.
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                            </CardContent>
                        </Card>
                </TabsContent>

                <TabsContent value="media" className="mt-0">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Media</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="thumbnailUrl"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Thumbnail</FormLabel>
                                            <FormControl>
                                                <ImageUploadDropzone
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    description="JPEG, PNG, WebP · max 5 MB · shown on stream cards"
                                                    aspectRatio="thumbnail"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="featureImageUrl"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Feature Banner</FormLabel>
                                            <FormControl>
                                                <ImageUploadDropzone
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    description="JPEG, PNG, WebP · max 5 MB · banner on stream page"
                                                    aspectRatio="banner"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>
                </TabsContent>

                <div className="flex flex-col gap-2">
                    {form.formState.errors.root && (
                        <p className="text-sm font-medium text-destructive">
                            {form.formState.errors.root.message}
                        </p>
                    )}
                    <div className="flex justify-between">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleBack}
                            disabled={isFirstStep}
                        >
                            <ChevronLeft className="mr-2 h-4 w-4" />
                            Back
                        </Button>
                        
                        {isLastStep ? (
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    initialData ? 'Save Changes' : 'Create Stream'
                                )}
                            </Button>
                        ) : (
                            <Button type="button" onClick={handleNext}>
                                Next
                                <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </form>
            </Tabs>
        </Form>
    );
}
